import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { getTimestampForTPlus } from "../lib/get-timestamp-for-t-plus";
import {
  ExternalConnection,
  UserProfile,
  UserProfileDocument,
} from "../types/User";

export async function updateUserProfileHandle(
  userId: string,
  username: string,
  usernameUpdateCount: number
) {
  // Check if username already exists:
  const usernameExistenceQuery = query(
    collection(firestore, "profiles"),
    where("username", "==", username)
  );
  const existenceQuerySnapshot = await getDocs(usernameExistenceQuery);
  if (!existenceQuerySnapshot.empty) {
    throw new Error("This username already in use");
  }

  // Run the below in a transaction:
  await runTransaction(firestore, async (transaction) => {
    // Update the user document's usernameUpdateCount and nextUsernameUpdateAt:
    const userRef = doc(firestore, "users", userId);
    transaction.update(userRef, {
      usernameUpdateCount: increment(1),
      nextUsernameUpdateAt: getTimestampForTPlus(
        Math.pow(2, usernameUpdateCount)
      ),
    });

    const profileRef = doc(firestore, "profiles", userId);
    transaction.update(profileRef, {
      username,
      updatedAt: serverTimestamp(),
    });
  });
}

export async function updateUserZipCode(userId: string, zipCode: string) {
  const profileDocRef = doc(firestore, "profiles", userId);
  await updateDoc(profileDocRef, { zipCode, updatedAt: serverTimestamp() });
}

export async function updateUserSocials(
  userId: string,
  socials: ExternalConnection[]
) {
  const profileDocRef = doc(firestore, "profiles", userId);
  await updateDoc(profileDocRef, { socials, updatedAt: serverTimestamp() });
}

export async function getUserProfile(
  userId: string
): Promise<UserProfile | null> {
  const profileDocRef = doc(firestore, "profiles", userId);
  const profileDocSnap = await getDoc(profileDocRef);

  if (profileDocSnap.exists()) {
    const data = profileDocSnap.data() as UserProfileDocument;
    return {
      userId: data.userId,
      username: data.username,
      createdAt: new Date(data.createdAt.toMillis()),
      photoURL: data.photoURL || undefined,
      zipCode: data.zipCode || undefined,
      pokesAvailable:
        data.pokesAvailable === undefined ? 5 : data.pokesAvailable,
    };
  }
  return null;
}

type UserProfileMap = Record<string, UserProfile>;
type BatchUserProfileFetchResult = {
  userProfileMap: UserProfileMap;
  userProfiles: UserProfile[];
};

export async function getUserProfilesByIds(
  userIds: string[]
): Promise<BatchUserProfileFetchResult> {
  const userProfilesMap: UserProfileMap = {};
  const userProfiles = await Promise.all(
    userIds.map((id) => getUserProfile(id))
  ).then((profiles) => profiles.filter((profile) => profile !== null));

  for (const userProfile of userProfiles) {
    if (userProfile) {
      userProfilesMap[userProfile.userId] = userProfile;
    }
  }

  return { userProfileMap: userProfilesMap, userProfiles };
}

/**
 * Placeholder function for poking a user.
 * This will eventually handle the Firestore transaction to:
 * - Decrement pokesAvailable for pokerUserId
 * - Increment pokesAvailable for pokedUserId
 * @param pokerUserId The ID of the user initiating the poke.
 * @param pokedUserId The ID of the user being poked.
 */
export async function pokeUser(
  pokerUserId: string,
  pokedUserId: string
): Promise<void> {
  console.log(
    `User ${pokerUserId} poked user ${pokedUserId}. This is a placeholder.`
  );
  // TODO: Implement Firestore transaction to update pokesAvailable counts.
  // try {
  //   await runTransaction(firestore, async (transaction) => {
  //     const pokerProfileRef = doc(firestore, "profiles", pokerUserId);
  //     const pokedProfileRef = doc(firestore, "profiles", pokedUserId);
  //     // Note: It's important to read the documents within the transaction
  //     // to ensure atomicity and handle potential race conditions.
  //     const pokerProfileDoc = await transaction.get(pokerProfileRef);
  //     const pokedProfileDoc = await transaction.get(pokedProfileRef);
  //     if (!pokerProfileDoc.exists() || !pokedProfileDoc.exists()) {
  //       throw new Error("One or both user profiles not found.");
  //     }
  //     const pokerPokesAvailable = pokerProfileDoc.data()?.pokesAvailable;
  //     if (pokerPokesAvailable === undefined || pokerPokesAvailable <= 0) {
  //       throw new Error("User has no pokes available.");
  //     }
  //     transaction.update(pokerProfileRef, {
  //       pokesAvailable: increment(-1),
  //       updatedAt: serverTimestamp(),
  //     });
  //     transaction.update(pokedProfileRef, {
  //       pokesAvailable: increment(1),
  //       updatedAt: serverTimestamp(),
  //     });
  //   });
  //   console.log(`Successfully poked user ${pokedUserId} by ${pokerUserId}.`);
  // } catch (error) {
  //   console.error("Error poking user:", error);
  //   throw new Error("Failed to poke user.");
  // }
}
